import { HttpsCallableResult, getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import DeviationExternalLinkData from "../ExternalLinks/DeviationExternalLinkData";
import BookingData from "../Property/Booking/BookingData";
import BookingUnitData from "../Property/Booking/BookingUnitData";
import { PropertyData } from "../Property/PropertyData";
import { UnitData } from "../Property/Units/UnitData";
import { UserData } from "../User/UserData";
import { FirebaseData } from "./FirebaseData";
import { LocalData } from "./LocalData";

type AuthTokenCollectionData<T extends FirebaseData> = "all" | T[];

interface AuthTokenPropertyData {
  data: PropertyData;
  deviations: AuthTokenCollectionData<DeviationExternalLinkData>;
  units: AuthTokenCollectionData<UnitData>;
  bookingUnits: AuthTokenCollectionData<BookingUnitData>;
  bookings: AuthTokenCollectionData<BookingData>;
  canCreateTasks: boolean;
}

export interface AuthenticationToken {
  customToken: string;
  navBarElements: string[];
  redirectURL: string;
  claims: {
    properties: AuthTokenPropertyData[];
  };
}

export class AuthenticationTokenData {
  static token?: AuthenticationToken;
  static secret?: string;

  // localhost:3000/external?token=3TIelz4OPRi68rHCe2Lv
  static generateAuthenticationToken(secret: string): Promise<AuthenticationToken> {
    AuthenticationTokenData.secret = secret;
    const functions = getFunctions(undefined, "europe-west1");
    const runFunction = httpsCallable(functions, "generateAuthenticationToken");
    const promise = new Promise((resolve: (value: AuthenticationToken) => void, reject) => {
      runFunction({
        authenticationSecret: secret,
      })
        .then((result: HttpsCallableResult<any>) => {
          AuthenticationTokenData.token = {
            customToken: result.data.customToken,
            redirectURL: result.data.redirectURL,
            navBarElements: result.data.navBarElements,
            claims: {
              properties: Object.keys(result.data.claims.properties).map((pId) => {
                const data = result.data.claims.properties[pId];
                const property = LocalData.properties.addData(pId);
                let deviations: DeviationExternalLinkData[] | "all" = [];
                if (data.deviations) {
                  if (data.deviations.all === true) {
                    deviations = "all";
                  } else {
                    deviations = [];
                    for (let deviationId in data.deviations) {
                      let deviation = property.deviations.addData(deviationId);
                      let externalDeviation = new DeviationExternalLinkData(deviationId, "externalLinks", deviation);
                      externalDeviation.deviation = deviation;
                      externalDeviation.setup(data.deviations[deviationId]);
                      deviations.push(externalDeviation);
                    }
                  }
                }
                return {
                  data: property,
                  deviations: deviations,
                  units:
                    data.units && typeof data.units.all === "boolean" ? "all" : Object.keys(data.units ?? {}).map((id) => property.getUnitWithId(id)),
                  bookingUnits:
                    data.bookingUnits && typeof data.bookingUnits.all === "boolean"
                      ? "all"
                      : Object.keys(data.bookingUnits ?? {}).map((id) => property.bookingUnits.addData(id)),
                  bookings:
                    data.bookings && typeof data.bookings.all === "boolean"
                      ? "all"
                      : Object.keys(data.bookings ?? {}).map((id) => property.bookings.addData(id)),
                  canCreateTasks: data.canCreateTasks ?? false,
                } satisfies AuthTokenPropertyData;
              }),
            },
          };

          resolve(AuthenticationTokenData.token!);
        })
        .catch((error) => {
          reject(error);
        });
    });
    return promise;
  }

  static getExternalDeviationDataWithId(id: string): undefined | DeviationExternalLinkData {
    if (this.token?.claims.properties) {
      for (let property of this.token?.claims.properties) {
        if (property.deviations instanceof Array) {
          for (let externalDeviation of property.deviations) {
            if (externalDeviation.deviation?.id === id) {
              return externalDeviation;
            }
          }
        }
      }
    }
    return undefined;
  }

  static signInWithCustomToken(token: AuthenticationToken): Promise<UserData> {
    const auth = getAuth();
    return signInWithCustomToken(auth, token.customToken).then((auth) => {
      if (auth.user) {
        LocalData.currentUser = LocalData.users.addData(auth.user.uid);
        LocalData.currentUser.properties = token.claims.properties.map((property) => property.data);
        LocalData.currentUser.isExternal = true;
        LocalData.currentUser.loaded = true;
        return LocalData.currentUser;
      }
      throw new Error("No user");
    });
  }
}
